import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  buildEffectivePermissionMap,
  type PermissionEffect,
  type PermissionKey,
} from '@/lib/permissions';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import type { OrgMemberPermissionOverrideInsert } from '@/types/database.types';

export function useOrgMemberPermissionOverrides() {
  const orgId = useOrgStore((s) => s.organization?.id);

  return useQuery({
    queryKey: ['org-member-permission-overrides', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_member_permission_overrides')
        .select('*')
        .eq('organization_id', orgId!)
        .order('permission_key', { ascending: true });

      if (error) throw error;
      return data;
    },
  });
}

export function useEffectivePermissions() {
  const orgId = useOrgStore((s) => s.organization?.id);
  const role = useOrgStore((s) => s.memberRole);
  const user = useAuthStore((s) => s.user);

  const overrides = useQuery({
    queryKey: ['effective-permissions', orgId, user?.id],
    enabled: !!orgId && !!user?.id && !!role,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('org_member_permission_overrides')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('user_id', user!.id);

      if (error) throw error;
      return data;
    },
  });

  const permissionMap = useMemo(
    () => buildEffectivePermissionMap(role, overrides.data ?? []),
    [overrides.data, role],
  );

  return {
    ...overrides,
    permissionMap,
    hasPermission: (permissionKey: PermissionKey) =>
      permissionMap.get(permissionKey)?.allowed ?? false,
  };
}

export function usePermission(permissionKey: PermissionKey) {
  const permissions = useEffectivePermissions();

  return {
    ...permissions,
    allowed: permissions.hasPermission(permissionKey),
  };
}

export function useSetMemberPermissionOverride() {
  const queryClient = useQueryClient();
  const orgId = useOrgStore((s) => s.organization?.id);
  const user = useAuthStore((s) => s.user);

  return useMutation({
    mutationFn: async ({
      memberId,
      targetUserId,
      permissionKey,
      effect,
    }: {
      memberId: string;
      targetUserId: string;
      permissionKey: PermissionKey;
      effect: PermissionEffect;
    }) => {
      const payload: OrgMemberPermissionOverrideInsert = {
        organization_id: orgId!,
        member_id: memberId,
        user_id: targetUserId,
        permission_key: permissionKey,
        effect,
        created_by_user_id: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('org_member_permission_overrides')
        .upsert(payload, {
          onConflict: 'organization_id,user_id,permission_key',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-member-permission-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['effective-permissions'] });
    },
  });
}

export function useClearMemberPermissionOverride() {
  const queryClient = useQueryClient();
  const orgId = useOrgStore((s) => s.organization?.id);

  return useMutation({
    mutationFn: async ({
      memberId,
      permissionKey,
    }: {
      memberId: string;
      permissionKey: PermissionKey;
    }) => {
      const { error } = await supabase
        .from('org_member_permission_overrides')
        .delete()
        .eq('organization_id', orgId!)
        .eq('member_id', memberId)
        .eq('permission_key', permissionKey);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-member-permission-overrides'] });
      queryClient.invalidateQueries({ queryKey: ['effective-permissions'] });
    },
  });
}
